import { Stack } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionsMenu,
  LinkAsButton,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
} from 'melp-design/components';
import { useConfirm } from 'store/confirm';
import { usePredefinedToasts } from 'utils/Toast';
import {
  useCustomCategories,
  useDeleteCustomCategory,
} from 'store/custom-categories';
import { useModalContext } from 'store/modal';
import {
  CreateCustomCategoryModal,
  CreateCustomCategoryValueModal,
  CustomCategoryValuesModal,
  EditCustomCategoryModal,
} from 'containers/custom-categories';
import { useMe } from 'state/Administrators';

export const CustomCategoriesList: FC = () => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const predefinedToasts = usePredefinedToasts();
  const { hasPermissionScope } = useMe();

  const { data: customCategories, isLoading } = useCustomCategories();

  const { mutate: deleteCustomCategory, isLoading: isDeleting } =
    useDeleteCustomCategory();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <ListTotal total={customCategories?.length} />

            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('custom_employee_category.new_employee_category'),
                  color: 'primary',
                  onClick: () => {
                    openModal({
                      component: CreateCustomCategoryModal,
                    });
                  },
                },
              ]}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={customCategories ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('common.name'),
            cell: ({ row: { original: category } }) => {
              return (
                <LinkAsButton
                  label={category.name}
                  onClick={() => {
                    openModal({
                      component: CustomCategoryValuesModal,
                      props: {
                        categoryId: category.id,
                        categoryName: category.name,
                      },
                      size: 'lg',
                    });
                  }}
                />
              );
            },
          },
          {
            id: 'actions',
            cell: ({ row: { original: category } }) => {
              return (
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('actions.add_new_value'),
                      onClick: () => {
                        openModal({
                          component: CreateCustomCategoryValueModal,
                          props: {
                            categoryId: category.id,
                            categoryName: category.name,
                          },
                        });
                      },
                    },
                    {
                      label: t('common.edit'),
                      onClick: () => {
                        openModal({
                          component: EditCustomCategoryModal,
                          props: {
                            categoryId: category.id,
                            initialValues: {
                              name: category.name,
                            },
                          },
                        });
                      },
                      disabled: hasPermissionScope,
                    },
                    {
                      label: t('common.remove'),
                      onClick: async () => {
                        const { confirmed } = await confirmation(
                          t('common.areYouSureToDelete', {
                            title: category.name,
                          }),
                        );

                        if (confirmed) {
                          deleteCustomCategory(
                            { categoryId: category.id },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.deleted();
                              },
                            },
                          );
                        }
                      },
                      color: 'error',
                      disabled: isDeleting || hasPermissionScope,
                    },
                  ]}
                />
              );
            },
            meta: { align: 'right' },
          },
        ]}
      />
    </TableWrapper>
  );
};
